import * as Sentry from '@sentry/vue';
import { ref } from 'vue';
import type { Ref } from 'vue';
import type {
  EventEngagement,
  InterestRung,
  InterestSource,
} from '@@/types/api';
import { ApiError } from '~/composables/api/api-error';
import { useEventInterestApi } from '~/composables/api/use-event-interest-api';
import { useMyInterests } from '~/stores/use-my-interests';

interface UseInterestActionsOptions {
  source: InterestSource;
  eventId: Ref<string>;
  myRung: Ref<InterestRung | null>; // the control binds here
  interestedCount: Ref<number>;
  goingCount: Ref<number>;
  // title/kind/timing for the optimistic store upsert
  engagementMeta?: () => Partial<EventEngagement>;
  // native: enrich(); almanac detail: refetch the public resource. Omitted by
  // card callers, which keep the optimistic counts.
  refresh?: () => Promise<void>;
}

/**
 * Shared optimistic orchestration for setting/clearing interest across both the
 * native and Almanac sources. Owns pending + snackbar state; the control binds
 * `myRung`/counts and calls `setInterest`.
 */
export function useInterestActions(options: UseInterestActionsOptions) {
  const {
    source,
    eventId,
    myRung,
    interestedCount,
    goingCount,
    engagementMeta,
    refresh,
  } = options;

  const interestApi = useEventInterestApi();
  const myInterests = useMyInterests();
  const { trackEvent } = useMatomo();

  const isPending = ref(false);

  // Snackbar state
  const snackbar = ref(false);
  const snackbarText = ref('');
  const snackbarColor = ref('success');

  function showSnackbar(text: string, color = 'success') {
    snackbarText.value = text;
    snackbarColor.value = color;
    snackbar.value = true;
  }

  // Move the count between buckets. Leaving a rung decrements its bucket
  // (clamped at >= 0); entering a rung increments its bucket.
  function applyCountDelta(
    from: InterestRung | null,
    to: InterestRung | null,
  ): void {
    if (from === 'interested') {
      interestedCount.value = Math.max(0, interestedCount.value - 1);
    } else if (from === 'going') {
      goingCount.value = Math.max(0, goingCount.value - 1);
    }

    if (to === 'interested') {
      interestedCount.value += 1;
    } else if (to === 'going') {
      goingCount.value += 1;
    }
  }

  async function setInterest(target: InterestRung | null): Promise<void> {
    // "Going" is never a native declared rung — it is derived from a commitment.
    // Ideas have no "Going" rung at all (nothing concrete to be going to yet).
    // Emitting it for either source is a programming error; surface it loudly
    // rather than sending an impossible request.
    if ((source === 'native' || source === 'idea') && target === 'going') {
      throw new Error(
        'Native interest cannot be set to "going" — it is derived from a commitment, never declared.',
      );
    }

    // Debounce double-clicks while a request is in flight.
    if (isPending.value) return;

    const prev = {
      rung: myRung.value,
      interested: interestedCount.value,
      going: goingCount.value,
    };

    // No-op (re-selecting the current rung).
    if (prev.rung === target) return;

    // --- Optimistic apply ---
    applyCountDelta(prev.rung, target);
    myRung.value = target;
    myInterests.localSet(eventId.value, source, target, engagementMeta?.());

    isPending.value = true;
    try {
      if (source === 'native') {
        if (target === 'interested') {
          const res = await interestApi.setNativeInterest(eventId.value);
          myRung.value = res.declared_rung; // always 'interested'
        } else {
          // target === null
          await interestApi.clearNativeInterest(eventId.value);
          myRung.value = null;
        }
      } else if (source === 'idea') {
        // Ideas are "Interested"-only (body-less PUT/DELETE, like native). The
        // top-of-function guard already rejected `going`, so target is
        // 'interested' or null here — never fall through to the almanac branch.
        if (target === 'interested') {
          const res = await interestApi.setIdeaInterest(eventId.value);
          myRung.value = res.declared_rung; // always 'interested'
        } else {
          // target === null
          await interestApi.clearIdeaInterest(eventId.value);
          myRung.value = null;
        }
      } else if (target === null) {
        await interestApi.clearAlmanacInterest(eventId.value);
        myRung.value = null;
      } else {
        const res = await interestApi.setAlmanacInterest(eventId.value, target);
        myRung.value = res.declared_rung;
      }

      // True up authoritative counts on surfaces that can refetch (detail pages).
      // Almanac responses carry no counts, and cards omit `refresh` entirely —
      // both keep the optimistic values.
      if (refresh) await refresh();

      trackEvent('Interest', target ? 'Set' : 'Cleared', eventId.value);
    } catch (e) {
      // --- Revert optimistic state ---
      myRung.value = prev.rung;
      interestedCount.value = prev.interested;
      goingCount.value = prev.going;
      myInterests.localSet(
        eventId.value,
        source,
        prev.rung,
        engagementMeta?.(),
      );

      if (e instanceof ApiError && e.isValidation) {
        showSnackbar(
          'This event has ended — you can no longer save it.',
          'error',
        );
      } else if (e instanceof ApiError && e.isNetworkError) {
        showSnackbar(
          'Network error — check your connection and try again.',
          'error',
        );
      } else {
        Sentry.captureException(e, {
          tags: {
            operation: `interest_${target ? 'set' : 'clear'}`,
            source,
            event_id: eventId.value,
          },
        });
        showSnackbar('Something went wrong. Please try again.', 'error');
      }
    } finally {
      isPending.value = false;
    }
  }

  return {
    isPending,
    snackbar,
    snackbarText,
    snackbarColor,
    setInterest,
  };
}
